import React, { useEffect, useId, useRef, useState } from 'react';

export interface ResourceMenuItem {
  id: string;
  label: string;
  onSelect?: () => void;
  href?: string;
  external?: boolean;
  tone?: 'default' | 'danger';
}

interface ResourceActionsMenuProps {
  items: ResourceMenuItem[];
  ariaLabel?: string;
}

export function ResourceActionsMenu({ items, ariaLabel = 'Más acciones' }: ResourceActionsMenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (!items.length) return null;

  return (
    <div ref={rootRef} className="relative shrink-0">
      <button
        type="button"
        className="studio-resource-menu-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={ariaLabel}
        onClick={() => setOpen((value) => !value)}
      >
        <span aria-hidden>⋮</span>
      </button>

      {open ? (
        <div id={menuId} role="menu" className="studio-resource-menu">
          {items.map((item) => {
            const className = `studio-resource-menu__item${
              item.tone === 'danger' ? ' studio-resource-menu__item--danger' : ''
            }`;

            if (item.href) {
              return (
                <a
                  key={item.id}
                  role="menuitem"
                  href={item.href}
                  className={className}
                  target={item.external ? '_blank' : undefined}
                  rel={item.external ? 'noreferrer' : undefined}
                  onClick={() => setOpen(false)}
                >
                  {item.label}
                </a>
              );
            }

            return (
              <button
                key={item.id}
                type="button"
                role="menuitem"
                className={className}
                onClick={() => {
                  item.onSelect?.();
                  setOpen(false);
                }}
              >
                {item.label}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
